/**
 * SSO Service
 *
 * Handles Single Sign-On provider interactions on the client side.
 * Fetches available providers and initiates OAuth flows.
 */

import { createLogger } from '../utils/logger'

const logger = createLogger('ssoService')

const SSO_BASE_URL = '/auth/sso'

/**
 * Fetch available SSO providers
 * @returns {Promise<{providers: Array<{id: string, name: string, icon: string}>}>}
 */
export async function getAvailableProviders() {
  try {
    const response = await fetch(`${SSO_BASE_URL}/providers`)

    if (!response.ok) {
      throw new Error('Failed to fetch SSO providers')
    }

    const data = await response.json()
    return data
  } catch (error) {
    logger.error('SSO service error:', error)
    return { providers: [] }
  }
}

/**
 * Get the URL for SSO login with a specific provider
 * @param {string} provider - Provider ID (clever, classlink, google_edu)
 * @returns {string} The SSO login URL
 */
export function getLoginUrl(provider) {
  return `${SSO_BASE_URL}/${provider}`
}

/**
 * Initiate SSO login by redirecting to the provider
 * @param {string} provider - Provider ID
 */
export function initiateLogin(provider) {
  window.location.href = getLoginUrl(provider)
}

/**
 * Check if any SSO providers are available
 * @returns {Promise<boolean>}
 */
export async function hasAvailableProviders() {
  const { providers } = await getAvailableProviders()
  return providers && providers.length > 0
}

/**
 * Get provider display info for UI rendering
 * @param {string} providerId - Provider ID
 * @returns {Object} Provider display info
 */
export function getProviderInfo(providerId) {
  const providers = {
    clever: {
      id: 'clever',
      name: 'Clever',
      icon: 'clever',
      color: '#4285F4',
      bgColor: 'bg-blue-600',
      hoverBgColor: 'hover:bg-blue-700',
      textColor: 'text-white'
    },
    classlink: {
      id: 'classlink',
      name: 'ClassLink',
      icon: 'classlink',
      color: '#00A4E4',
      bgColor: 'bg-cyan-600',
      hoverBgColor: 'hover:bg-cyan-700',
      textColor: 'text-white'
    },
    google_edu: {
      id: 'google_edu',
      name: 'Google Workspace',
      icon: 'google',
      color: '#4285F4',
      bgColor: 'bg-white',
      hoverBgColor: 'hover:bg-gray-50',
      textColor: 'text-gray-700',
      border: true
    }
  }

  return (
    providers[providerId] || {
      id: providerId,
      name: providerId,
      icon: 'key',
      bgColor: 'bg-gray-600',
      hoverBgColor: 'hover:bg-gray-700',
      textColor: 'text-white'
    }
  )
}

export default {
  getAvailableProviders,
  getLoginUrl,
  initiateLogin,
  hasAvailableProviders,
  getProviderInfo
}
